import stringMixin from "../../../mixins/general/helpers/stringMixin";
import {getCookie, setCookie} from "../../../modules/appUtils";

const TEST_SESSION_HASH_ID_COOKIE_NAME = 'test_session';
const TEST_SESSION_HASH_ID_COOKIE_LIFETIME = 60 * 60 * 24 * 7; // 7 дней


const state = {
    isTestManagementPageShown: false,
    currentComponent: false,
    isUserCanCreateTest: false,
    isAuth: false,
    trans: {},
    locale: 'ru',
    routes: {},
    testable: {
        type: null,
        id: null,
    },
    tests: {},
    requestsProgress: {
        test: {
            index: false,
            store: false,
            edit: false,
            update: false,
            delete: false,
            updateSettings: false,
            show: false,
        },
        question: {
            store: false,
            update: false,
            delete: false,
            updateSettings: false,
        },
        session: {
            index: false,
        },
    },
    testEditor: {
        test: null,
        parameters: null,
        settings: null,
        errors: {},
        settingsErrors: {},
        isCanEdit: false,
        session: {},
        sessionMaintainInterval: 0,
        notAllowReplayUsers: {},
        editorComponent: 'test-edit-form',
    },
    testTaker: {
        test: null,
        question: null,
        parameters: null,
        session: null,
        answer: null,
        failMessage: null,
        isCanStartNewSession: false,
    },
    testStatistic: {
        test: null,
        question: null,
        user: null,
        users: {},
        questions: null,
        usersForQuestion: {},
        sessions: null,
        session: {},
        sessionDetails: null,
        isShowSkipQuestions: false,
    },
    isFullScreenTest: false,
    isPublicForm: false,
    modals: {
        confirmation: {
            title: null,
            text: null,
            confirmationButtonText: null,
            cancelButtonText: null,
            confirmationHandler: null,
            cancelHandler: null,
            colorClass: null,
        },
        error: {
            title: null,
            text: null,
            cancelButtonText: null,
            cancelHandler: null,
            colorClass: null,
        }
    },
};

const getters = {
    isTestManagementPageShown: state => {
        return state.isTestManagementPageShown;
    },
    isPublicForm: state => {
        return state.isPublicForm;
    },
    isAuth: state => {
        return state.isAuth;
    },
    currentComponent: state => {
        return state.currentComponent;
    },
    editorComponent: state => {
        return state.testEditor.editorComponent;
    },
    isUserCanCreateTest: state => {
        return state.isUserCanCreateTest;
    },
    trans: state => {
        return state.trans;
    },
    routes: state => {
        return state.routes;
    },
    locale: state => {
        return state.locale;
    },
    tests: state => {
        return state.tests;
    },
    requestsProgress: state => {
        return state.requestsProgress;
    },
    isAnyRequestInProgress: state => {
        for (let [category, requests] of  Object.entries(state.requestsProgress)) {
            for (let [request, value] of Object.entries(requests)) {
                if (value === true) {
                    return true;
                }
            }
        }

        return false;
    },
    testable: state => {
        return state.testable;
    },
    testBeingEdited: state => {
        return state.testEditor.test;
    },
    testSettings: state => {
        return state.testEditor.settings;
    },
    testEditor: state => {
        return state.testEditor;
    },
    modals: state => {
        return state.modals;
    },
    testTaker: state => {
        return state.testTaker;
    },
    testBeingPassing: state => {
        return state.testTaker.test;
    },
    testBeingPassingSession: state => {
        return state.testTaker.session;
    },
    isCanStartNewSession: state => {
        return state.testTaker.isCanStartNewSession;
    },
    questionBeingPassing: state => {
        return state.testTaker.question;
    },
    isCompleteQuestionBeingPassing: state => {
        return state.testTaker.question
            && state.testTaker.question.userAnswer
            && state.testTaker.question.userAnswer.length > 0;
    },
    failMessage: state => {
        return state.testTaker.failMessage;
    },
    testStatistic: state => {
        return state.testStatistic;
    },
    testStatisticUsers: state => {
        return state.testStatistic.users.closed ?? {};
    },
    testStatisticUsersUnclosed: state => {
        return state.testStatistic.users.unclosed ?? {};
    },
    testStatisticSessions: state => {
        return state.testStatistic.sessions;
    },
    testStatisticSession: state => {
        return state.testStatistic.session;
    },
    testStatisticUsersForQuestion: state => {
        return state.testStatistic.usersForQuestion ?? {};
    },
    testStatisticQuestions: state => {
        return state.testStatistic.questions;
    },
    testStatisticSessionDetails: state => {
        const isShowSkipQuestions = state.testStatistic.isShowSkipQuestions;
        const sessionDetails = state.testStatistic.sessionDetails ?? [];

        return isShowSkipQuestions ?
            sessionDetails :
            sessionDetails.filter(question => !question.is_skip);
    },
    isSessionHasSkipQuestions: state => {
        const sessionDetails = state.testStatistic.sessionDetails ?? [];

        return sessionDetails.filter(question => question.is_skip).length > 0;
    },
    isHasSessionDetailsQuestionsWithPoints: state => {
        const sessionDetails = state.testStatistic.sessionDetails ?? [];

        return sessionDetails.some(item => item.is_has_points);
    },
    testStatisticQuestion: state => {
        return state.testStatistic.question;
    },
    testStatisticUser: state => {
        return state.testStatistic.user;
    },
    isAllQuestionsIsRequired: state => {
        const test = state.testEditor.test;
        const questions = Object.values(test.questions ?? []);

        return !questions.length > 0 ||
            questions.length === questions.filter(item => item.settings.is_required).length;
    },
    isAllQuestionsAnswersIsRandomOrder: state => {
        const test = state.testEditor.test;
        const questions = Object.values(test.questions ?? []);

        return questions.length > 0 &&
            questions.length === questions.filter(item => item.settings.is_random_order).length;
    },
    isAllQuestionsIsHasPoints: state => {
        const test = state.testEditor.test;
        const questions = Object.values(test.questions ?? []);

        return questions.length > 0 &&
            questions.length === questions.filter(item => item.settings.is_has_points).length;
    },
    isShowSkipQuestions: state => {
        return state.testStatistic.isShowSkipQuestions;
    },
    isFullScreenTest: state => {
        return state.isFullScreenTest;
    },
    isCanEdit: state => {
        return state.testEditor.isCanEdit;
    },
    editSession: state => {
        return state.testEditor.session;
    },
    sessionMaintainInterval: state => {
        return state.testEditor.sessionMaintainInterval;
    },
    settingsErrors: state => {
        return state.testEditor.settingsErrors;
    }
};

const actions = {
    setTestable: ({commit}, testable) => {
        commit('setTestable', testable);
    },
    showTestsManagementPage: ({commit}) => {
        commit('showTestsManagementPage');
    },
    hideTestsManagementPage: ({commit}) => {
        commit('hideTestsManagementPage');
    },

    /**
     * Установка объекта локализации
     * @param commit
     * @param trans
     */
    setTrans: ({commit}, trans) => {
        commit('setTrans', trans);
    },

    /**
     * Установка объекта роутов
     * @param commit
     * @param routes
     */
    setRoutes: ({commit}, routes) => {
        commit('setRoutes', routes);
    },

    setLocale: ({commit}, locale) => {
        commit('setLocale', locale);
    },

    /**
     * Установка текущего компонента отображаемого для управления тестами
     * @param commit
     * @param component
     */
    setCurrentComponent: ({commit}, component) => {
        commit('setCurrentComponent', component);
    },

    enableRequestInProgress: ({commit}, request) => {
        commit('setRequestIsInProgress', {
            request: request,
            status: true,
        })
    },

    disableRequestInProgress: ({commit}, request) => {
        commit('setRequestIsInProgress', {
            request: request,
            status: false,
        });
    },

    loadTestsList: ({commit, getters, dispatch}) => {
        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.index, {
            testable_type: getters.testable.type,
            testable_id: getters.testable.id,
        });

        dispatch('enableRequestInProgress', 'test.index');

        axios.get(endpoint)
            .then(({data}) => {
                if (typeof data !== "object") {
                    return;
                }

                commit('setTestList', data);
            })
            .finally(() => dispatch('disableRequestInProgress', 'test.index'));
    },

    setIsPublicForm: ({commit}, value) => {
        commit('setIsPublicForm', value);
    },

    setIsAuth: ({commit}, value) => {
        commit('setIsAuth', value);
    },

    returnToTestListPage: ({commit, dispatch, getters}) => {
        commit('setCurrentComponent', 'test-list');
        commit('setTestBeingPassing', null);
        commit('setQuestionBeingPassing', null);
        commit('setTestTakerParameters', null);
        commit('setTestBeingPassingSession', null);
        commit('setTestTakerAnswer', null);
        commit('setStatisticTest', null);
        commit('setStatisticUsers', {});
        commit('setStatisticQuestions', null);
    },

    setTestTakerAnswer: ({commit}, answer) => {
        commit('setTestTakerAnswer', answer);
    },

    setIsUserCanCreateTest: ({commit}, isUserCanCreateTest) => {
        commit('setIsUserCanCreateTest', isUserCanCreateTest);
    },

    setIsShowSkipQuestions: ({commit, getters}, value) => {
        commit('setIsShowSkipQuestions', value);
    },

    toggleFullscreen: ({commit, getters}) => {
        let isFullScreen = !getters.isFullScreenTest;
        commit('setIsFullScreenTest', isFullScreen);
    },

    errorHandler: ({commit, dispatch}, response) => {
        if (response.status === 510) {
            dispatch('showErrorModal', response.data.message)
        }
    },

    /**
     * Редактирование теста
     */
    createTest: ({commit, getters, dispatch}) => {
        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.store, {
            testable_type: getters.testable.type,
            testable_id: getters.testable.id,
        });

        dispatch('enableRequestInProgress', 'test.store');

        axios.post(endpoint)
            .then(({data}) => {
                dispatch('editTest', data);
                commit('addTest', data);
            })
            .finally(() => dispatch('disableRequestInProgress', 'test.store'));
    },

    getTestDataForEditor: ({commit, getters, dispatch}, testId) => {
        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.edit, {
            test_id: testId,
        });

        dispatch('enableRequestInProgress', 'test.edit')

        return axios.get(endpoint)
            .finally(() => dispatch('disableRequestInProgress', 'test.edit'));
    },

    editTest: ({commit, dispatch}, test) => {
        dispatch('getTestDataForEditor', test.hash_id)
            .then(({data}) => {
                commit('setTestBeingEdited', data.test)
                commit('setTestEditorParameters', data.editor_parameters)
                commit('setTestEditorSettings', data.settings)
                commit('setIsCanEdit', data.is_can_edit);
                commit('setSession', data.session);
                commit('setSessionMaintainInterval', data.session_maintain_interval);
                commit('setCurrentComponent', 'test-editor')
                commit('setEditorComponent', 'test-edit-form')
            });
    },

    editSessionMaintain: ({getters}, test) => {
        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.editSession.maintain, {
            test_id: test.hash_id,
        });

        return axios.patch(endpoint);
    },

    editSessionDestroy: ({getters}, test) => {
        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.editSession.destroy, {
            test_id: test.hash_id,
        });

        return axios.delete(endpoint);
    },

    updateTest: ({commit, dispatch, getters}, test) => {
        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.update, {
            test_id: test.hash_id,
        });

        dispatch('enableRequestInProgress', 'test.update');

        return axios.patch(endpoint, test)
            .then(({data}) => {
                commit('updateTest', data);
            })
            .catch(({response}) => {
                dispatch('errorHandler', response);
            })
            .finally(() => dispatch('disableRequestInProgress', 'test.update'));
    },

    updateTestSettings: ({commit, dispatch, getters}, {test, settings}) => {
        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.updateSettings, {
            test_id: test.hash_id,
        });

        dispatch('enableRequestInProgress', 'test.updateSettings');
        return axios.patch(endpoint, settings)
            .then(({data}) => {
                commit('setTestEditorSettings', data);
            })
            .catch(({response}) => {
                if (response.data && response.data.errors) {
                    commit('setSettingsErrors', response.data.errors);
                }
                dispatch('errorHandler', response);
            })
            .finally(() => dispatch('disableRequestInProgress', 'test.updateSettings'));
    },

    clearSettingsErrors: ({commit}) => {
        commit('setSettingsErrors', {});
    },

    toggleActiveTest: ({commit, dispatch, getters}, {test, value}) => {
        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.setActive, {
            test_id: test.hash_id,
        });

        dispatch('enableRequestInProgress', 'test.setActive');

        test.is_active = value;

        return axios.patch(endpoint, test)
            .then(({data}) => {
                test.is_active = data.is_active;
                commit('updateTest', test);
            })
            .catch(({response}) => {
                test.is_active = !value;
                dispatch('errorHandler', response);
            })
            .finally(() => dispatch('disableRequestInProgress', 'test.setActive'));
    },

    deleteTest: ({commit, dispatch, getters}, test) => {
        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.delete, {
            test_id: test.hash_id,
        });

        dispatch('enableRequestInProgress', 'test.delete');

        return axios.delete(endpoint)
            .then(({data}) => {
                if (data.is_deleted) {
                    commit('deleteTest', test);
                }
            })
            .catch(({response}) => {
                dispatch('errorHandler', response);
            })
            .finally(() => dispatch('disableRequestInProgress', 'test.delete'));
    },

    createTestQuestion: ({commit, dispatch, getters}, questionType) => {
        if (!getters.testBeingEdited || !getters.isCanEdit) return;
        const testBeingEdited = getters.testBeingEdited;

        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.question.storeEmpty, {
            test_id: getters.testBeingEdited.hash_id
        });

        const settings = {
            "is_random_order":getters.isAllQuestionsAnswersIsRandomOrder,
            "is_required":getters.isAllQuestionsIsRequired,
            "is_has_points":getters.isAllQuestionsIsHasPoints,
        };

        dispatch('enableRequestInProgress', 'question.store');

        axios.post(endpoint, {
            'type': questionType.code,
            'sort': (Object.keys(testBeingEdited.questions).length * 500) + 500,
        }).then(({data}) => {
            const newQuestion = data.result;
            commit('addQuestion', newQuestion);

            dispatch('setQuestionBeingEdited', {
                questionId: newQuestion.hash_id,
                isBeingEdited: true,
            });

            commit('setTestQuestionSettings', {
                questionId: newQuestion.hash_id,
                settings: settings
            })
        }).finally(() => {
            dispatch('disableRequestInProgress', 'question.store')
        })
    },

    updateTestQuestion: ({commit, dispatch, getters}, {questionId, questionData}) => {
        if (!getters.testBeingEdited || !getters.isCanEdit) return;

        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.question.update, {
            test_id: getters.testBeingEdited.hash_id,
            question_id: questionId,
        });

        let settings = getters.testBeingEdited.questions[questionId]
            ? getters.testBeingEdited.questions[questionId].settings
            : {"is_random_order":false,"is_required":false,"is_has_points":false};

        dispatch('enableRequestInProgress', 'question.update');
        questionData['settings'] = settings;

        axios.patch(endpoint, questionData)
            .then(({data}) => {
                if (data.hash_id !== questionId) {
                    commit('deleteQuestion', {hash_id: questionId});
                    commit('addQuestion', data);
                } else {
                    commit('updateTestQuestion', {id: questionId, data: data})
                }
            })
            .catch(({ response }) => {
                if (response.status === 422) {
                    commit('setTestEditorErrors', {errors: response.data.errors, questionId});
                }
                dispatch('errorHandler', response);
            })
            .finally(() => dispatch('disableRequestInProgress', 'question.update'));
    },

    updateTestQuestionSettings: ({commit, dispatch, getters}, {questionId, settings}) => {
        if (!getters.testBeingEdited || !getters.isCanEdit) return;
        const question = getters.testBeingEdited.questions[questionId];

        if (!question || question.is_unsaved) {
            return;
        }

        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.question.updateSettings, {
            test_id: getters.testBeingEdited.hash_id,
            question_id: questionId,
        });

        dispatch('enableRequestInProgress', 'question.updateSettings');

        axios.patch(endpoint, settings)
            .then(({data}) => {
                if (data.hash_id === questionId) {
                    commit('updateTestQuestion', {id: questionId, data: data})
                    dispatch('setQuestionBeingEdited', {questionId: questionId, isBeingEdited: true});
                }
            })
            .catch(({ response }) => {
                if (response.status === 422) {
                    commit('setTestEditorErrors', {errors: response.data.errors, questionId});
                }
                dispatch('errorHandler', response);
            })
            .finally(() => dispatch('disableRequestInProgress', 'question.updateSettings'));

    },

    updateTestAllQuestionAnswersRandomOrder: ({commit, dispatch, getters}, value) => {
        if (!getters.testBeingEdited || !getters.isCanEdit) return;

        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.updateAllQuestionAnswersRandomOrder, {
            test_id: getters.testBeingEdited.hash_id,
        });

        let data = {'is_random_order': value};
        dispatch('enableRequestInProgress', 'test.updateSettings');

        axios.patch(endpoint, data)
            .then(({data}) => {
                for (let [questionId, settings] of  Object.entries(data.settings)) {
                    commit('setTestQuestionSettings', {questionId, settings});
                }
            })
            .finally(() => dispatch('disableRequestInProgress', 'test.updateSettings'));
    },

    updateTestAllQuestionIsRequired: ({commit, dispatch, getters}, value) => {
        if (!getters.testBeingEdited || !getters.isCanEdit) return;

        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.updateAllQuestionsIsRequired, {
            test_id: getters.testBeingEdited.hash_id,
        });

        let data = {'is_required': value};
        dispatch('enableRequestInProgress', 'test.updateSettings');

        axios.patch(endpoint, data)
            .then(({data}) => {
                for (let [questionId, settings] of  Object.entries(data.settings)) {
                    commit('setTestQuestionSettings', {questionId, settings});
                }
            })
            .finally(() => dispatch('disableRequestInProgress', 'test.updateSettings'));
    },

    updateTestAllQuestionsIsHasPoints: ({commit, dispatch, getters}, value) => {
        if (!getters.testBeingEdited || !getters.isCanEdit) return;

        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.updateTestAllQuestionsIsHasPoints, {
            test_id: getters.testBeingEdited.hash_id,
        });

        let data = {'is_has_points': value};
        dispatch('enableRequestInProgress', 'test.updateSettings');

        axios.patch(endpoint, data)
            .then(({data}) => {
                for (let [questionId, settings] of  Object.entries(data.settings)) {
                    commit('setTestQuestionSettings', {questionId, settings});
                }
            })
            .finally(() => dispatch('disableRequestInProgress', 'test.updateSettings'));
    },

    deleteQuestion: ({commit, dispatch, getters}, question) => {
        if (!getters.testBeingEdited || !getters.isCanEdit) return;

        if (question.is_unsaved) {
            commit('deleteQuestion', question);
            return;
        }

        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.question.delete, {
            test_id: getters.testBeingEdited.hash_id,
            question_id: question.hash_id,
        });

        dispatch('enableRequestInProgress', 'question.delete');

        axios.delete(endpoint)
            .then(({data}) => {
                if (data.is_deleted) {
                    commit('deleteQuestion', question)
                    commit('setTestEditorIsActive', data.test_is_active);
                }
            })
            .finally(() => dispatch('disableRequestInProgress', 'question.delete'));
    },

    clearTestQuestionError: ({commit}, questionId) => {
        commit('deleteTestEditorErrors', questionId);
    },

    addAnswer: ({commit, getters}, questionId) => {
        if (!getters.isCanEdit) {
            return;
        }
        commit('addAnswer', questionId);
    },

    deleteAnswer: ({commit}, {questionId, answerIndex}) => {
        commit('deleteAnswer', {questionId, answerIndex});
    },

    setQuestionBeingEdited: ({commit}, {questionId, isBeingEdited}) => {
        commit('setQuestionBeingEdited', {questionId, isBeingEdited});
    },

    showNotAllowReplayTestUsers: ({commit}) => {
        commit('setEditorComponent', 'test-allow-replay-users-table');
    },

    loadNotAllowReplayUsers: ({commit, dispatch, getters}, test) => {

        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.notAllowReplayUsers, {
            test_id: test.hash_id,
        });

        axios.get(endpoint)
            .then(({data}) => {
                commit('setNotAllowReplayTestUsers', data.result);
            });
    },

    allowUserToTestReplay: ({commit, getters}, {test, email}) => {
        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.allowUserToReplay, {
            test_id: test.hash_id,
        });

        axios.patch(endpoint , {
            email: email,
        }).then(({data}) => {
            if (data.result) {
                commit('deleteUserNotAllowReplay', email);
            }
        });
    },

    backToEditTest: ({commit}) => {
        commit('setEditorComponent', 'test-edit-form');
    },

    /**
     * Редактирование теста END
     */


    /**
     * Прохождение теста
     */
    showPublicTestForm: ({commit, dispatch, getters}, test) => {
        let urlData = { test_id: test.hash_id };

        let sessionHashId = getCookie(TEST_SESSION_HASH_ID_COOKIE_NAME);

        if (sessionHashId) {
            urlData['session_id'] = sessionHashId;
        }

        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.public.session.index, urlData);

        return axios.get(endpoint)
            .then(({data}) => {
                commit('setTestBeingPassingSession', data.session ?? null);
                commit('setCanStartNewSession', data.is_can_start_new_session ?? false);
                commit('setTestBeingPassing', test);
                commit('setCurrentComponent', 'test-start-form');
            })
            .catch(({ response }) =>  {
                if (response.status === 404) {
                    commit('setTestBeingPassingSession', null);
                    commit('setCanStartNewSession', false);
                    commit('setTestBeingPassing', test);
                    commit('setCurrentComponent', 'test-start-form');
                }
            })
            .finally(() => dispatch('disableRequestInProgress', 'session.index'));
    },

    showStartConfirmationPage: ({commit, dispatch, getters}, test) => {
        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.public.session.index, {
            test_id: test.hash_id,
        });
        dispatch('enableRequestInProgress', 'session.index');

        return axios.get(endpoint)
            .then(({data}) => {
                commit('setTestBeingPassingSession', data.session ?? null);
                commit('setCanStartNewSession', data.is_can_start_new_session ?? false);
                commit('setTestBeingPassing', test);
                commit('setCurrentComponent', 'test-start-form');
            })
            .finally(() => dispatch('disableRequestInProgress', 'session.index'));
    },

    storeRespondent: ({commit, dispatch, getters}, {name, email, authIgnore}) => {
        const test = getters.testTaker.test;
        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.public.respondent.store, {
            test_id: test.hash_id,
        });

        let sessionHashId = getCookie(TEST_SESSION_HASH_ID_COOKIE_NAME) ?? '';

        return axios.post(endpoint, {
            name: name,
            email: email,
            auth_ignore: authIgnore,
            session_hash_id: sessionHashId
        })
            .then(({data}) => {
                if (!data.question) {
                    let trans = getters.trans.fail_form.errors;
                    dispatch('showTestFailPage', trans.not_has_questions);
                } else {
                    dispatch('saveSessionHashIdInCookie', data.session.hash_id);
                    commit('setTestBeingPassing', data.test);
                    commit('setQuestionBeingPassing', data.question);
                    commit('setTestTakerParameters', data.parameters);
                    commit('setTestBeingPassingSession', data.session);
                    commit('setCurrentComponent', 'test-show-form');
                }
            });
    },

    startTest: ({commit, dispatch, getters}, {test, forceNew}) => {
        let isNeedRespondentForm = !test.settings.is_can_anonymous
            && forceNew
            && getters.isPublicForm
            && !getters.isAuth;

        if (isNeedRespondentForm) {
            commit('setCurrentComponent', 'test-respondent-form');

            return;
        }

        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.public.show, {
            test_id: test.hash_id,
        });

        let sessionHashId = getCookie(TEST_SESSION_HASH_ID_COOKIE_NAME) ?? '';
        dispatch('enableRequestInProgress', 'test.show')

        return axios.get(endpoint, {
            params: {
                force_new: forceNew,
                session_hash_id: sessionHashId,
            }
        })
            .then(({data}) => {
                if (!data.question) {
                    let trans = getters.trans.fail_form.errors;
                    dispatch('showTestFailPage', trans.not_has_questions);
                } else {
                    dispatch('saveSessionHashIdInCookie', data.session.hash_id);
                    commit('setTestBeingPassing', data.test);
                    commit('setQuestionBeingPassing', data.question);
                    commit('setTestTakerParameters', data.parameters);
                    commit('setTestBeingPassingSession', data.session);
                    commit('setCurrentComponent', 'test-show-form');
                }
            })
            .finally(() => dispatch('disableRequestInProgress', 'test.show'));
    },

    saveSessionHashIdInCookie: ({}, sessionHashId) => {
        setCookie(TEST_SESSION_HASH_ID_COOKIE_NAME, sessionHashId, TEST_SESSION_HASH_ID_COOKIE_LIFETIME);
    },

    showTestResultPage: ({getters, dispatch, commit}) => {
        const session = getters.testTaker.session;
        const test = getters.testTaker.test;

        commit('setCurrentComponent', 'test-result-page');
        commit('setStatisticSession', session);

        dispatch('loadTestSessionResult', {test, session});
    },

    showTestFailPage: ({commit}, message) => {
        commit('setFailMessage', message);
        commit('setCurrentComponent', 'test-fail-form');
    },

    setQuestionUserAnswer: ({commit}, userAnswer) => {
        commit('setQuestionUserAnswer', userAnswer);
    },

    sendAnswerAndContinue: ({commit, dispatch, getters}) => {
        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.public.question.answer, {
            test_id: getters.testBeingPassing.hash_id,
            question_id: getters.questionBeingPassing.hash_id,
            session_id: getters.testBeingPassingSession.hash_id,
        });

        const variants = getters.testTaker.question.answers;
        const hashVariants = variants ? variants.map(item => item.hash_id) : [];

        dispatch('enableRequestInProgress', 'question.answer')
        commit('setFailMessage', null);

        return axios.post(endpoint, {
            answer: getters.questionBeingPassing.userAnswer,
            variants: hashVariants,
        })
            .then(({data}) => {
                dispatch('showNextQuestion', data);
            })
            .catch(({response}) => {
                if (response.status === 422 && response.data.errors.answer) {
                    commit('setFailMessage', response.data.errors.answer[0]);
                }

                if (response.status === 404) {
                    let trans = getters.trans;
                    dispatch('showTestFailPage', trans.errors.cannot_answer);
                }
            })
            .finally(() => dispatch('disableRequestInProgress', 'question.answer'));
    },

    skipAnswerAndContinue: ({commit, dispatch, getters}) => {
        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.public.question.skip, {
            test_id: getters.testBeingPassing.hash_id,
            question_id: getters.questionBeingPassing.hash_id,
            session_id: getters.testBeingPassingSession.hash_id,
        });

        dispatch('enableRequestInProgress', 'question.answer')

        return axios.post(endpoint, {})
            .then(({data}) => {
                dispatch('showNextQuestion', data);
            })
            .catch(({response}) => {
                if (response.status === 404) {
                    let trans = getters.trans;
                    dispatch('showTestFailPage', trans.errors.cannot_answer);
                }
            })
            .finally(() => dispatch('disableRequestInProgress', 'question.answer'));
    },

    showNextQuestion: ({commit, dispatch, getters}, data) => {
        if (data.error && !data.session) {
            dispatch('showTestFailPage', data.error);

            return;
        }

        if (data.session.stat.total === data.session.stat.answered || !data.next_question) {
            commit('setCurrentComponent', 'test-result-form');
        } else {
            commit('setQuestionBeingPassing', data.next_question);
            commit('setTestBeingPassingSession', data.session);
            if (data.error) {
                commit('setFailMessage', data.error);
            }
        }
    },

    /**
     * Прохождение теста END
     */


    /**
     * Модальные окна
     */

    showAllowUserToReplayConfirmationModal: ({commit, getters}, {text, confirmationHandler, cancelHandler}) => {
        let trans = getters.trans.edit_form.modals;

        commit('setModalParameters', {
            modal: 'confirmation',
            parameters: {
                isShown: true,
                title: trans.allow_user_replay_title,
                text: text,
                confirmationButtonText: trans.allow,
                cancelButtonText: trans.cancel_button,
                confirmationHandler: confirmationHandler,
                cancelHandler: cancelHandler,
                colorClass: 'is-danger',
            },
        })
    },

    showDeleteConfirmationModal: ({commit, getters}, {text, confirmationHandler, cancelHandler}) => {
        let trans = getters.trans.edit_form.modals;

        commit('setModalParameters', {
            modal: 'confirmation',
            parameters: {
                isShown: true,
                title: trans.delete_confirmation_title,
                text: text,
                confirmationButtonText: trans.delete_button,
                cancelButtonText: trans.cancel_button,
                confirmationHandler: confirmationHandler,
                cancelHandler: cancelHandler,
                colorClass: 'is-danger',
            },
        })
    },

    showStoreRespondentConfirmation: ({commit, getters, dispatch}, {cancelHandler}) => {
        let trans = getters.trans.respondent_form.user_exists_modal;
        commit('setModalParameters', {
            modal: 'confirmation',
            parameters: {
                isShown: true,
                title: trans.title,
                text: trans.text,
                confirmationButtonText: trans.confirmation_button_text,
                cancelButtonText: trans.cancel_button_text,
                confirmationHandler: () => {
                    window.location.href = getters.routes.public.login;
                },
                cancelHandler: cancelHandler,
                colorClass: 'is-danger',
            },
        })
    },

    hideConfirmationModal: ({commit}) => {
        commit('setModalParameters', {
            modal: 'confirmation',
            parameters: {},
        });
    },

    showErrorModal: ({commit, getters, dispatch}, text) => {
        const trans = getters.trans.edit_form.modals;

        commit('setModalParameters', {
            modal: 'error',
            parameters: {
                isShown: true,
                title: trans.error_title,
                text: text,
                cancelButtonText: trans.ok,
                cancelHandler: () => {dispatch('hideErrorModal')},
                colorClass: 'is-danger',
            },
        })
    },

    hideErrorModal: ({commit}) => {
        commit('setModalParameters', {
            modal: 'error',
            parameters: {},
        });
    },

    /**
     * Модальные окна END
     */

    /**
     * Статистика по тестам
     */
    showTestStatistic: ({commit, dispatch}, test) => {
        commit('setCurrentComponent', 'test-statistics-tabs');
        commit('setStatisticTest', test);
        dispatch('loadTestStatisticsByUsers', test);
        dispatch('loadTestStatisticsByQuestions', test);
    },

    showTestStatisticsSessions: ({commit, dispatch, getters}, {user, isClosed}) => {
        commit('setCurrentComponent', 'test-statistics-sessions');
        commit('setStatisticUser', user);
        let test = getters.testStatistic.test;
        let question = getters.testStatistic.question;
        dispatch('loadTestStatisticsByUser', {test, user, question, isClosed});
    },

    showTestStatisticsUsersForQuestion: ({commit, dispatch, getters}, question) => {
        commit('setCurrentComponent', 'test-statistics-users-for-question');
        commit('setStatisticQuestion', question);
        let test = getters.testStatistic.test;
        dispatch('loadTestStatisticsForUsersByQuestion', {test, question});
    },

    showTestStatisticDetail: ({commit, dispatch, getters}, session) => {
        commit('setCurrentComponent', 'test-statistics-details');

        let test = getters.testStatistic.test;
        dispatch('loadTestStatisticsBySession', {test, session});
    },

    loadTestStatisticsByUsers: ({commit, getters}, test) => {
        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.statistics.users, {
            test_id: test.hash_id,
        });

        return axios.get(endpoint)
            .then(({data}) => {
                commit('setStatisticUsers', data.result ?? {});
            });
    },

    loadTestStatisticsByQuestions: ({commit, getters}, test) => {
        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.statistics.questions, {
            test_id: test.hash_id,
        });

        return axios.get(endpoint)
            .then(({data}) => {
                commit('setStatisticQuestions', data.result ?? null);
            });
    },

    loadTestStatisticsByUser: ({commit, getters}, {test, user, question, isClosed}) => {
        let parameters = {
            test_id: test.hash_id,
            respondent_type: user.type ?? '',
            respondent_id: user.hash_id ?? '',
            question_id: question ? question.hash_id : '',
            is_closed: isClosed
        };

        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.statistics.sessions, parameters);
        return axios.get(endpoint, {params: {
                is_closed: isClosed,
            }})
            .then(({data}) => {
                commit('setStatisticSessions', data.result ?? null);
            });
    },

    loadTestStatisticsForUsersByQuestion: ({commit, getters}, {test, question}) => {
        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.statistics.usersForQuestion, {
            test_id: test.hash_id,
            question_id: question.hash_id,
        });

        return axios.get(endpoint)
            .then(({data}) => {
                commit('setStatisticUsersForQuestion', data.result ?? {});
            });
    },

    loadTestStatisticsBySession: ({commit, getters}, {test, session}) => {
        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.statistics.sessionDetail, {
            test_id: test.hash_id,
            session_id: session.hash_id,
        });

        return axios.get(endpoint)
            .then(({data}) => {
                commit('setStatisticSessionDetails', data.result ?? null);
                commit('setStatisticSession', data.session ?? {});
            });
    },

    loadTestSessionResult: ({commit, getters}, {test, session}) => {
        let endpoint = stringMixin.methods.replaceWildcards(getters.routes.public.session.result, {
            test_id: test.hash_id,
            session_id: session.hash_id,
        });

        return axios.get(endpoint)
            .then(({data}) => {
                commit('setStatisticSessionDetails', data.result ?? null);
                commit('setStatisticSession', data.session ?? {});
            });
    },

    returnToTestStatisticsPage: ({commit}) => {
        commit('setCurrentComponent', 'test-statistics-tabs');
        commit('setStatisticSessions', null);
        commit('setStatisticUsersForQuestion', {});
        commit('setStatisticSessionDetails', null);
        commit('setStatisticUser', null);
        commit('setStatisticQuestion', null);
        commit('setStatisticSession', {});
    },

    /**
     * Статистика по тестам END
     */
};

const mutations = {
    setTestable: (state, testable) => {
        Vue.set(state, 'testable', testable);
    },
    showTestsManagementPage: (state) => {
        Vue.set(state, 'isTestManagementPageShown', true);
    },
    hideTestsManagementPage: (state) => {
        Vue.set(state, 'isTestManagementPageShown', false);
    },
    setTrans: (state, trans) => {
        Vue.set(state, 'trans', trans);
    },
    setRoutes: (state, routes) => {
        Vue.set(state, 'routes', routes);
    },
    setLocale: (state, locale) => {
        Vue.set(state, 'locale', locale);
    },
    setCurrentComponent: (state, component) => {
        Vue.set(state, 'currentComponent', component);
    },
    setEditorComponent: (state, component) => {
        Vue.set(state.testEditor, 'editorComponent', component);
    },
    setRequestIsInProgress: (state, {request, status}) => {
        let pathArray = request.split('.'),
            requestName = pathArray[pathArray.length - 1],
            getProperty = (o, i) => {
                let isLastPart = typeof o[i] !== 'object';

                return isLastPart ? o : o[i];
            },
            requestCategory = pathArray.reduce(getProperty, state.requestsProgress);

        Vue.set(requestCategory, requestName, status);
    },
    setTestList: (state, list) => {
        Vue.set(state, 'tests', list);
    },
    addTest: (state, test) => {
        Vue.set(state.tests, test.hash_id, test)
    },
    updateTest: (state, test) => {
        Vue.set(state.tests, test.hash_id, test);
    },
    deleteTest: (state, test) => {
        Vue.delete(state.tests, test.hash_id);
    },
    addQuestion: (state, question) => {
        state.testEditor.test.questions = {...state.testEditor.test.questions};

        Vue.set(state.testEditor.test.questions, question.hash_id, question);
    },
    deleteQuestion: (state, question) => {
        Vue.delete(state.testEditor.test.questions, question.hash_id);
    },
    setTestBeingEdited: (state, test) => {
        Vue.set(state.testEditor, 'test', test);
    },
    setTestEditorParameters: (state, parameters) => {
        Vue.set(state.testEditor, 'parameters', parameters);
    },
    setTestEditorSettings: (state, settings) => {
        Vue.set(state.testEditor, 'settings', settings);
    },
    setTestEditorIsActive: (state, value) => {
        Vue.set(state.testEditor.test, 'is_active', value);
    },
    setNotAllowReplayTestUsers: (state, users) => {
        Vue.set(state.testEditor, 'notAllowReplayUsers', users);
    },
    setTestQuestionSettings: (state, {questionId, settings}) => {
        Vue.set(state.testEditor.test.questions[questionId], 'settings', settings);
    },
    addAnswer: (state, questionId) => {
        let question = state.testEditor.test.questions[questionId],
            answerId = _.uniqueId(`answer_${questionId}_`);

        if (Object.keys(question.answers ?? {}).length <= 0) {
            Vue.set(question, 'answers', {});
        }

        Vue.set(question.answers, answerId, {
            text: null,
            points: 0,
        });
    },
    deleteAnswer: (state, {questionId, answerIndex}) => {
        Vue.delete(state.testEditor.test.questions[questionId].answers, answerIndex);
    },
    updateTestQuestion: (state, {id, data}) => {
        data.answers = Object.assign({}, data.answers);

        Vue.set(state.testEditor.test.questions, id, data);
    },

    setQuestionBeingEdited: (state, {questionId, isBeingEdited}) => {
        Vue.set(state.testEditor.test.questions[questionId], 'isEditing', isBeingEdited);
    },

    setModalParameters: (state, {modal, parameters}) => {
        Vue.set(state.modals, modal, parameters);
    },

    setTestBeingPassing: (state, test) => {
        Vue.set(state.testTaker, 'test', test);
    },
    setQuestionBeingPassing: (state, question) => {
        Vue.set(state.testTaker, 'question', question);
    },
    setTestTakerParameters: (state, parameters) => {
        Vue.set(state.testTaker, 'parameters', parameters);
    },
    setTestBeingPassingSession: (state, session) => {
        Vue.set(state.testTaker, 'session', session);
    },
    setQuestionUserAnswer: (state, userAnswer) => {
        Vue.set(state.testTaker.question, 'userAnswer', userAnswer);
    },
    setTestEditorErrors: (state, {errors, questionId}) => {
        Vue.set(state.testEditor.errors, questionId, errors);
    },
    deleteTestEditorErrors: (statem, questionId) => {
        Vue.delete(state.testEditor.errors, questionId)
    },
    setTestTakerAnswer: (state, answer) => {
        Vue.set(state.testTaker, 'answer', answer);
    },
    setIsUserCanCreateTest: (state, isUserCanCreateTest) => {
        Vue.set(state, 'isUserCanCreateTest', isUserCanCreateTest);
    },
    setFailMessage: (state, message) => {
        Vue.set(state.testTaker, 'failMessage', message);
    },
    setCanStartNewSession: (state, value) => {
        Vue.set(state.testTaker, 'isCanStartNewSession', value);
    },
    setStatisticTest: (state, test) => {
        Vue.set(state.testStatistic, 'test', test);
    },
    setStatisticQuestion: (state, question) => {
        Vue.set(state.testStatistic, 'question', question);
    },
    setStatisticUsers: (state, users) => {
        if (!users) {
            users = {};
        }

        Vue.set(state.testStatistic, 'users', users);
    },
    setStatisticUser: (state, user) => {
        Vue.set(state.testStatistic, 'user', user);
    },
    setStatisticQuestions: (state, questions) => {
        Vue.set(state.testStatistic, 'questions', questions);
    },
    setStatisticUsersForQuestion: (state, users) => {
        if (!users) {
            users = {};
        }
        Vue.set(state.testStatistic, 'usersForQuestion', users);
    },
    setStatisticSessions: (state, sessions) => {
        Vue.set(state.testStatistic, 'sessions', sessions);
    },
    setStatisticSession: (state, session) => {
        Vue.set(state.testStatistic, 'session', session);
    },
    setStatisticSessionDetails: (state, session) => {
        Vue.set(state.testStatistic, 'sessionDetails', session);
    },
    setIsShowSkipQuestions: (state, value) => {
        Vue.set(state.testStatistic, 'isShowSkipQuestions', value);
    },
    setIsFullScreenTest: (state, value) => {
        Vue.set(state, 'isFullScreenTest', value);
    },
    setIsCanEdit: (state, value) => {
        Vue.set(state.testEditor,  'isCanEdit', value);
    },
    setSession: (state, {name, email}) => {
        Vue.set(state.testEditor, 'session', {name, email});
    },
    setSessionMaintainInterval: (state, value) => {
        Vue.set(state.testEditor, 'sessionMaintainInterval', value);
    },
    deleteUserNotAllowReplay: (state, email) => {
        Vue.delete(state.testEditor.notAllowReplayUsers, email);
    },
    setSettingsErrors: (state, errors) => {
        Vue.set(state.testEditor, 'settingsErrors', errors);
    },
    setIsPublicForm: (state, value) => {
        Vue.set(state, 'isPublicForm', value);
    },
    setIsAuth: (state, value) => {
        Vue.set(state, 'isAuth', value);
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
